/**
 * Throwaway media cards backed by a static JSON snapshot pulled from the
 * gt-mono Neon DB. Renders the full "detail" content of a book / news story /
 * podcast episode / video so we can sense-check whether re-integrating these
 * modules is worthwhile before wiring up GraphQL.
 */

import { useEffect, useRef, useState } from 'react';
import { BookOpen, Loader2, Pause, Play, Mic } from 'lucide-react';
import { BottomSheet, ResizedImage } from '@ah/design/mobile';

type Author = { name: string };
type BookChapter = {
  title: string;
  keyTakeaway?: string;
  blocks?: { kind: string; body: string; source?: string | null }[];
};

export type BookItem = {
  kind: 'book';
  id: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  coverUrl: string | null;
  firstPublishYear: number | null;
  authors: Author[];
  summary: {
    hook: string | null;
    themes: string[] | null;
    youllLikeThisIf: string[] | null;
    chapters: BookChapter[] | null;
    readingMinutesEst: number | null;
  };
};

export type NewsItem = {
  kind: 'news';
  id: string;
  title: string;
  snippet: string | null;
  imageUrl: string | null;
  sourceName: string | null;
  topics: { name: string; id: string | null }[] | null;
  articleCount: number | null;
  publishedAt: string | null;
  summary: {
    whatHappened?: string;
    eli5?: string;
    longRead?: string[];
    keyQuotes?: { quote: string; speaker: string; sourceName?: string | null }[];
    keyStats?: { stat: string; context: string; sourceName?: string | null }[];
    keyPeople?: { name: string; role: string }[];
    whyItMatters?: string;
    relatedTopics?: string[];
    sourcesCited?: { title: string; url: string; sourceName?: string | null }[];
  } | null;
  articles: {
    title: string;
    snippet: string | null;
    sourceName: string | null;
    articleUrl: string;
    publishedAt: string | null;
    imageUrl: string | null;
  }[];
};

export type PodcastItem = {
  kind: 'podcast';
  id: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  duration: number | null;
  datePublished: string | null;
  audioUrl: string | null;
  episodeNumber: number | null;
  seasonNumber: number | null;
  series: { name: string | null; author: string | null; imageUrl: string | null } | null;
  summary: {
    hook?: string;
    whatItsAbout?: string;
    eli5?: string;
    longRead?: string[];
    keyQuotes?: { quote: string; speaker: string; timestampSec: number | null }[];
    keyIdeas?: { title: string; body: string }[];
    keyPeople?: { name: string; role: string | null }[];
    whyItMatters?: string;
    chapters?: { title: string; startSec: number; body: string | null }[];
    topics?: string[];
  } | null;
};

export type VideoItem = {
  kind: 'video';
  id: string;
  youtubeId: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  duration: number | null;
  viewCount: number | null;
  datePublished: string | null;
  channel: { title: string | null; imageUrl: string | null; handle: string | null } | null;
  summary: {
    hook?: string;
    whatItsAbout?: string;
    eli5?: string;
    longRead?: string[];
    keyTakeaways?: string[];
    keyConcepts?: { term: string; wikipediaQuery: string; why: string }[];
    keySpeakers?: { name: string; bio: string }[];
    keyIdeas?: { title: string; body: string }[];
    keyQuotes?: { quote: string; speaker: string; timestampSec: number | null }[];
    whyItMatters?: string;
    chapters?: { title: string; startSec: number; body: string | null }[];
    topics?: string[];
  } | null;
};

export type MediaItem = BookItem | NewsItem | PodcastItem | VideoItem;

export function mediaCardTitle(item: MediaItem): string {
  switch (item.kind) {
    case 'book':
      return 'I curated this book for you — is it useful?';
    case 'news':
      return 'I curated this news story for you — is it useful?';
    case 'podcast':
      return 'I curated this podcast episode for you — is it useful?';
    case 'video':
      return 'I curated this video for you — is it useful?';
  }
}

function formatDuration(seconds: number | null | undefined): string | null {
  if (!seconds || seconds <= 0) return null;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm === 0 ? `${h}h` : `${h}h ${mm}m`;
}

function formatTimestamp(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = s.toString().padStart(2, '0');
  if (h > 0) {
    const mm = m.toString().padStart(2, '0');
    return `${h}:${mm}:${ss}`;
  }
  return `${m}:${ss}`;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h4 className="text-[10px] uppercase tracking-wider font-semibold text-primary/80">{title}</h4>
      {children}
    </div>
  );
}

function Hero({ src, alt }: { src: string | null; alt: string }) {
  if (!src) return null;
  return (
    <div className="w-full aspect-video bg-muted overflow-hidden">
      <ResizedImage src={src} renderWidth={384} fallback={false} alt={alt} loading="lazy" className="w-full h-full object-cover" />
    </div>
  );
}

function BookBody({ item, onRead }: { item: BookItem; onRead?: (book: BookItem) => void }) {
  const s = item.summary;
  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      <div className="w-full bg-primary/10 px-6 py-7 flex items-center justify-center">
        {item.coverUrl ? (
          <ResizedImage
            src={item.coverUrl}
            renderWidth={160}
            fallback={false}
            alt={item.title}
            loading="lazy"
            className="w-40 h-60 object-cover rounded-md shadow-xl bg-muted"
          />
        ) : (
          <div className="w-40 h-60 rounded-md shadow-md bg-muted flex items-center justify-center">
            <BookOpen size={36} className="text-muted-foreground/50" />
          </div>
        )}
      </div>
      <div className="p-6 space-y-5">
        <div className="space-y-1.5">
          <p className="font-bold text-xl leading-tight">{item.title}</p>
          {item.subtitle && <p className="text-sm text-muted-foreground">{item.subtitle}</p>}
          {item.authors.length > 0 && (
            <p className="text-[13px] text-muted-foreground">
              by {item.authors.map((a) => a.name).join(', ')}
              {item.firstPublishYear ? ` · ${item.firstPublishYear}` : ''}
            </p>
          )}
          {s.readingMinutesEst != null && (
            <p className="text-[12px] text-muted-foreground">{s.readingMinutesEst} min read</p>
          )}
        </div>

        {onRead && (
          <button
            type="button"
            onClick={() => onRead(item)}
            className="w-full flex items-center justify-center gap-2 h-12 rounded-full bg-primary text-primary-foreground text-sm font-semibold active:scale-[0.98] transition-all"
          >
            <BookOpen size={16} />
            Read Now
          </button>
        )}

        {s.hook && (
          <div className="border-l-2 border-primary/40 pl-4 py-1">
            <p className="text-[15px] text-foreground/90 leading-relaxed font-medium">{s.hook}</p>
          </div>
        )}

        {s.youllLikeThisIf && s.youllLikeThisIf.length > 0 && (
          <Section title="You'll like this if">
            <ul className="space-y-1.5 list-disc pl-5">
              {s.youllLikeThisIf.map((line, i) => (
                <li key={i} className="text-[14px] text-foreground/85 leading-relaxed">
                  {line}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {s.chapters && s.chapters.length > 0 && (
          <Section title={`Chapters (${s.chapters.length})`}>
            <ol className="space-y-3">
              {s.chapters.map((ch, i) => (
                <li key={i} className="space-y-1">
                  <p className="text-[14px] font-semibold text-foreground">
                    {i + 1}. {ch.title}
                  </p>
                  {ch.keyTakeaway && (
                    <p className="text-[14px] text-foreground/80 leading-relaxed">{ch.keyTakeaway}</p>
                  )}
                </li>
              ))}
            </ol>
          </Section>
        )}

        {s.themes && s.themes.length > 0 && (
          <Section title="Themes">
            <div className="flex flex-wrap gap-1.5">
              {s.themes.map((t, i) => (
                <span
                  key={i}
                  className="text-[11px] uppercase tracking-wide text-muted-foreground bg-muted rounded-full px-2 py-0.5"
                >
                  {t}
                </span>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

export function BookReader({ book }: { book: BookItem }) {
  const s = book.summary;
  return (
    <div className="px-5 pt-4 pb-6 space-y-6">
      <div className="flex gap-4">
        {book.coverUrl && (
          <ResizedImage
            src={book.coverUrl}
            renderWidth={80}
            fallback={false}
            alt={book.title}
            className="w-20 h-30 object-cover rounded-md shadow-md shrink-0 bg-muted"
          />
        )}
        <div className="flex-1 min-w-0 space-y-1">
          <p className="font-bold text-lg leading-tight">{book.title}</p>
          {book.subtitle && (
            <p className="text-sm text-muted-foreground">{book.subtitle}</p>
          )}
          {book.authors.length > 0 && (
            <p className="text-[13px] text-muted-foreground">
              by {book.authors.map((a) => a.name).join(', ')}
              {book.firstPublishYear ? ` · ${book.firstPublishYear}` : ''}
            </p>
          )}
          {s.readingMinutesEst != null && (
            <p className="text-[12px] text-muted-foreground">{s.readingMinutesEst} min read</p>
          )}
        </div>
      </div>

      {s.hook && (
        <div className="border-l-2 border-primary/40 pl-4 py-1">
          <p className="text-[15px] text-foreground/90 leading-relaxed font-medium">{s.hook}</p>
        </div>
      )}

      {s.chapters && s.chapters.length > 0 && (
        <div className="space-y-7">
          {s.chapters.map((ch, i) => (
            <section key={i} className="space-y-3">
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-primary/80">
                  Chapter {i + 1}
                </p>
                <h2 className="text-[18px] font-bold leading-snug text-foreground">{ch.title}</h2>
              </div>
              {ch.keyTakeaway && (
                <p className="text-[14px] text-foreground/80 leading-relaxed italic border-l-2 border-border pl-3">
                  {ch.keyTakeaway}
                </p>
              )}
              {ch.blocks && ch.blocks.length > 0 && (
                <div className="space-y-3">
                  {ch.blocks.map((b, j) => (
                    <p key={j} className="text-[15px] text-foreground/90 leading-relaxed">
                      {b.body}
                    </p>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

export function BookReaderSheet({
  book,
  open,
  onClose,
}: {
  book: BookItem | null;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <BottomSheet
      open={open && !!book}
      onClose={onClose}
      title={book?.title ?? 'Reading'}
      fullHeight
    >
      {book && <BookReader book={book} />}
    </BottomSheet>
  );
}

function NewsBody({ item }: { item: NewsItem }) {
  const s = item.summary ?? {};
  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      <Hero src={item.imageUrl} alt={item.title} />
      <div className="p-6 space-y-5">
        <div className="space-y-1.5">
          <p className="font-bold text-xl leading-tight">{item.title}</p>
          {item.articleCount ? (
            <p className="text-[13px] text-muted-foreground">
              Based on {item.articleCount} {item.articleCount === 1 ? 'source' : 'sources'}
            </p>
          ) : null}
        </div>

        {s.whatHappened && (
          <div className="border-l-2 border-primary/40 pl-4 py-1">
            <p className="text-[15px] text-foreground/90 leading-relaxed font-medium">
              {s.whatHappened}
            </p>
          </div>
        )}

        {s.longRead && s.longRead.length > 0 && (
          <Section title="In detail">
            <div className="space-y-3">
              {s.longRead.slice(0, 3).map((p, i) => (
                <p key={i} className="text-[15px] text-foreground/85 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </Section>
        )}

        {s.keyQuotes && s.keyQuotes.length > 0 && (
          <Section title="Key quotes">
            <ul className="space-y-3">
              {s.keyQuotes.slice(0, 3).map((q, i) => (
                <li key={i} className="border-l-2 border-border pl-3 py-0.5 space-y-0.5">
                  <p className="text-[14px] text-foreground/85 italic leading-relaxed">“{q.quote}”</p>
                  <p className="text-[12px] text-muted-foreground">
                    — {q.speaker}
                    {q.sourceName ? ` · ${q.sourceName}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {s.keyStats && s.keyStats.length > 0 && (
          <Section title="By the numbers">
            <ul className="space-y-2">
              {s.keyStats.slice(0, 4).map((st, i) => (
                <li key={i}>
                  <p className="text-[14px] font-semibold text-foreground">{st.stat}</p>
                  <p className="text-[13px] text-muted-foreground leading-snug">{st.context}</p>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {s.whyItMatters && (
          <Section title="Why it matters">
            <p className="text-[15px] text-foreground/85 leading-relaxed">{s.whyItMatters}</p>
          </Section>
        )}

        {item.articles.length > 0 && (
          <Section title="Coverage">
            <ul className="space-y-2">
              {item.articles.slice(0, 4).map((a, i) => (
                <li key={i} className="text-[13px]">
                  <p className="text-foreground/90 leading-snug">{a.title}</p>
                  {a.sourceName && (
                    <p className="text-muted-foreground text-[12px]">{a.sourceName}</p>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </div>
  );
}

interface PodcastAudioController {
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  playing: boolean;
  loading: boolean;
  errored: boolean;
  toggle: () => void;
  seek: (seconds: number) => void;
}

function usePodcastAudio(audioUrl: string | null): PodcastAudioController {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (!audioUrl) return;
    const audio = new Audio(audioUrl);
    audio.preload = 'none';
    audioRef.current = audio;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);
    const onWaiting = () => setLoading(true);
    const onPlaying = () => setLoading(false);
    const onCanPlay = () => setLoading(false);
    const onError = () => {
      setErrored(true);
      setPlaying(false);
      setLoading(false);
    };
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('error', onError);
    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('error', onError);
      audio.pause();
      audio.src = '';
      audioRef.current = null;
    };
  }, [audioUrl]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio || errored) return;
    if (audio.paused) {
      setLoading(true);
      const r: unknown = audio.play();
      if (r && typeof (r as Promise<void>).then === 'function') {
        (r as Promise<void>).then(
          () => { /* onPlay fires */ },
          () => {
            setErrored(true);
            setPlaying(false);
            setLoading(false);
          },
        );
      }
    } else {
      audio.pause();
    }
  };

  const seek = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, seconds);
    if (audio.paused) {
      setLoading(true);
      const r: unknown = audio.play();
      if (r && typeof (r as Promise<void>).then === 'function') {
        (r as Promise<void>).then(
          () => { /* onPlay fires */ },
          () => {
            setErrored(true);
            setLoading(false);
          },
        );
      }
    }
  };

  return { audioRef, playing, loading, errored, toggle, seek };
}

function PodcastHero({ item, audio }: { item: PodcastItem; audio: PodcastAudioController }) {
  const imageUrl = item.imageUrl ?? item.series?.imageUrl ?? null;
  const audioUrl = item.audioUrl;
  const disabled = !audioUrl || audio.errored;

  return (
    <button
      type="button"
      aria-label={audio.playing ? `Pause "${item.title}"` : `Play "${item.title}"`}
      onClick={audio.toggle}
      disabled={disabled}
      className="relative block w-full aspect-square bg-muted overflow-hidden focus:outline-none group"
    >
      {imageUrl ? (
        <ResizedImage
          src={imageUrl}
          renderWidth={384}
          fallback={false}
          alt={item.title}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center">
          <Mic size={56} className="text-muted-foreground/40" />
        </span>
      )}
      <span
        aria-hidden="true"
        className="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors group-hover:bg-black/25"
      >
        <span
          className={`flex items-center justify-center w-16 h-16 rounded-full shadow-lg ${
            disabled ? 'bg-black/40 text-white/70' : 'bg-black/70 text-white'
          }`}
        >
          {audio.loading ? (
            <Loader2 size={30} strokeWidth={1.75} className="animate-spin" />
          ) : audio.playing ? (
            <Pause size={30} strokeWidth={1.5} fill="currentColor" />
          ) : (
            <Play size={30} strokeWidth={1.5} className="ml-1" fill="currentColor" />
          )}
        </span>
      </span>
    </button>
  );
}

function PodcastBody({ item }: { item: PodcastItem }) {
  const s = item.summary ?? {};
  const audio = usePodcastAudio(item.audioUrl);
  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      <PodcastHero item={item} audio={audio} />
      <div className="p-6 space-y-5">
        <div className="space-y-1.5">
          <p className="font-bold text-lg leading-tight">{item.title}</p>
          {item.series?.name && (
            <p className="text-[13px] text-muted-foreground">{item.series.name}</p>
          )}
          <p className="text-[12px] text-muted-foreground">
            {[
              formatDuration(item.duration),
              item.episodeNumber ? `Ep ${item.episodeNumber}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>

        {s.hook && (
          <div className="border-l-2 border-primary/40 pl-4 py-1">
            <p className="text-[15px] text-foreground/90 leading-relaxed font-medium">{s.hook}</p>
          </div>
        )}

        {s.whatItsAbout && (
          <Section title="What it's about">
            <p className="text-[15px] text-foreground/85 leading-relaxed">{s.whatItsAbout}</p>
          </Section>
        )}

        {s.longRead && s.longRead.length > 0 && (
          <Section title="In detail">
            <div className="space-y-3">
              {s.longRead.slice(0, 3).map((p, i) => (
                <p key={i} className="text-[15px] text-foreground/85 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </Section>
        )}

        {s.keyIdeas && s.keyIdeas.length > 0 && (
          <Section title="Key ideas">
            <ul className="space-y-3">
              {s.keyIdeas.slice(0, 3).map((k, i) => (
                <li key={i} className="space-y-0.5">
                  <p className="text-[14px] font-semibold text-foreground">{k.title}</p>
                  <p className="text-[14px] text-foreground/80 leading-relaxed">{k.body}</p>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {s.keyQuotes && s.keyQuotes.length > 0 && (
          <Section title="Key quotes">
            <ul className="space-y-3">
              {s.keyQuotes.slice(0, 3).map((q, i) => (
                <li key={i} className="border-l-2 border-border pl-3 py-0.5 space-y-0.5">
                  <p className="text-[14px] text-foreground/85 italic leading-relaxed">“{q.quote}”</p>
                  <p className="text-[12px] text-muted-foreground">— {q.speaker}</p>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {s.chapters && s.chapters.length > 0 && (
          <Section title={`Chapters (${s.chapters.length})`}>
            <ol className="space-y-1">
              {s.chapters.map((c, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => audio.seek(c.startSec)}
                    disabled={!item.audioUrl || audio.errored}
                    className="w-full flex items-baseline gap-3 text-left text-[13px] text-foreground/85 py-1 rounded active:bg-muted/60 disabled:cursor-default"
                  >
                    <span className="text-[12px] tabular-nums text-primary font-semibold shrink-0 w-12">
                      {formatTimestamp(c.startSec)}
                    </span>
                    <span className="font-medium">{c.title}</span>
                  </button>
                </li>
              ))}
            </ol>
          </Section>
        )}

        {s.topics && s.topics.length > 0 && (
          <Section title="Topics">
            <div className="flex flex-wrap gap-1.5">
              {s.topics.map((t, i) => (
                <span
                  key={i}
                  className="text-[11px] uppercase tracking-wide text-muted-foreground bg-muted rounded-full px-2 py-0.5"
                >
                  {t}
                </span>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

function VideoHero({ item }: { item: VideoItem }) {
  // Lazy-load the YouTube iframe — keeps the card light until the user
  // actually wants to watch. Tap the thumbnail (with the play overlay)
  // and the image swaps for an autoplaying iframe in the same slot.
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <div className="w-full aspect-video bg-black overflow-hidden">
        <iframe
          src={`https://www.youtube.com/embed/${item.youtubeId}?autoplay=1&playsinline=1`}
          title={item.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      aria-label={`Play "${item.title}"`}
      onClick={() => setPlaying(true)}
      className="relative block w-full aspect-video bg-muted overflow-hidden focus:outline-none"
    >
      {item.imageUrl ? (
        <ResizedImage
          src={item.imageUrl}
          renderWidth={384}
          fallback={false}
          alt={item.title}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      ) : null}
      <span
        aria-hidden="true"
        className="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors group-hover:bg-black/25"
      >
        <span className="flex items-center justify-center w-14 h-14 rounded-full bg-black/70 text-white shadow-lg">
          <Play size={28} strokeWidth={1.5} className="ml-0.5" fill="currentColor" />
        </span>
      </span>
    </button>
  );
}

function VideoBody({ item }: { item: VideoItem }) {
  const s = item.summary ?? {};
  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      <VideoHero item={item} />
      <div className="p-6 space-y-5">
        <div className="space-y-1.5">
          <p className="font-bold text-lg leading-tight">{item.title}</p>
          <p className="text-[13px] text-muted-foreground">
            {item.summary?.topics?.[0] ?? item.channel?.title ?? 'YouTube'}
            {item.duration ? ` · ${formatDuration(item.duration)}` : ''}
          </p>
        </div>

        {s.hook && (
          <div className="border-l-2 border-primary/40 pl-4 py-1">
            <p className="text-[15px] text-foreground/90 leading-relaxed font-medium">{s.hook}</p>
          </div>
        )}

        {s.whatItsAbout && (
          <Section title="What it's about">
            <p className="text-[15px] text-foreground/85 leading-relaxed">{s.whatItsAbout}</p>
          </Section>
        )}

        {s.keyTakeaways && s.keyTakeaways.length > 0 && (
          <Section title="Key takeaways">
            <ul className="space-y-1.5 list-disc pl-5">
              {s.keyTakeaways.slice(0, 5).map((t, i) => (
                <li key={i} className="text-[14px] text-foreground/85 leading-relaxed">
                  {t}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {s.keyIdeas && s.keyIdeas.length > 0 && (
          <Section title="Key ideas">
            <ul className="space-y-3">
              {s.keyIdeas.slice(0, 3).map((k, i) => (
                <li key={i} className="space-y-0.5">
                  <p className="text-[14px] font-semibold text-foreground">{k.title}</p>
                  <p className="text-[14px] text-foreground/80 leading-relaxed">{k.body}</p>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {s.longRead && s.longRead.length > 0 && (
          <Section title="In detail">
            <div className="space-y-3">
              {s.longRead.slice(0, 3).map((p, i) => (
                <p key={i} className="text-[15px] text-foreground/85 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </Section>
        )}

        {s.chapters && s.chapters.length > 0 && (
          <Section title={`Chapters (${s.chapters.length})`}>
            <ol className="space-y-1.5">
              {s.chapters.map((c, i) => (
                <li key={i} className="text-[13px] text-foreground/85">
                  <span className="font-medium">{c.title}</span>
                </li>
              ))}
            </ol>
          </Section>
        )}

        {s.topics && s.topics.length > 0 && (
          <Section title="Topics">
            <div className="flex flex-wrap gap-1.5">
              {s.topics.map((t, i) => (
                <span
                  key={i}
                  className="text-[11px] uppercase tracking-wide text-muted-foreground bg-muted rounded-full px-2 py-0.5"
                >
                  {t}
                </span>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

export function MediaCard({
  item,
  onReadBook,
}: {
  item: MediaItem;
  onReadBook?: (book: BookItem) => void;
}) {
  switch (item.kind) {
    case 'book':
      return <BookBody item={item} onRead={onReadBook} />;
    case 'news':
      return <NewsBody item={item} />;
    case 'podcast':
      return <PodcastBody item={item} />;
    case 'video':
      return <VideoBody item={item} />;
  }
}
